import { ReactNode } from 'react';

import dayjs from 'dayjs';
import { DATE_TIME_WITH_OFFSET } from '@cast/constants';

import type { ComputeResourceType } from '@cast/shared-types';
import { WorkloadResourceConfig } from '@cast/types';
import { dashesOrNum } from '@cast/utils';
import { MetricsBox } from 'components/boxes';

import {
  computeResourceNameMap,
  computeResourceUnitsMap,
} from 'constants/maps';
import { WorkloadRecommendation } from 'features/workload-optimization/types';
import { formatLookbackPeriod } from '../../_utils';

import { Typography, Stack, tokens } from '@cast/design-system';

type LowConfidenceMetricProps = {
  title: string;
  value: ReactNode;
  testId: string;
  markerHeight?: number;
};

type LowConfidenceLookBackMetricProps = {
  type: ComputeResourceType;
  resourceConfig?: WorkloadResourceConfig;
};

type LowConfidenceLastRecommendationMetricProps = {
  type: ComputeResourceType;
  recommendation?: WorkloadRecommendation;
};

const LowConfidenceMetric = (props: LowConfidenceMetricProps) => {
  return (
    <MetricsBox
      markerColor="grey.600"
      markerProps={{ minHeight: props.markerHeight ?? 30 }}
      testId={props.testId}
      title={props.title}
      titleProps={{ variant: 'L10M', color: 'grey.400' }}
      primaryValue={props.value}
      primaryValueProps={{ variant: 'P12R' }}
      valuesContainerProps={{ gap: 4 }}
    />
  );
};

export const LowConfidenceLookBackMetric = ({
  resourceConfig,
  type,
}: LowConfidenceLookBackMetricProps) => {
  if (!resourceConfig) {
    return null;
  }

  return (
    <LowConfidenceMetric
      testId="look-back-period"
      title="look-back period"
      value={`${computeResourceNameMap[type]}: ${formatLookbackPeriod(
        resourceConfig
      )}`}
    />
  );
};

export const LowConfidenceLastRecommendationMetric = ({
  recommendation,
  type,
}: LowConfidenceLastRecommendationMetricProps) => {
  if (!recommendation?.latest) {
    return null;
  }

  return (
    <LowConfidenceMetric
      testId="last-recommendation"
      title="last recommendation applied"
      markerHeight={50}
      value={
        <Stack gap={4}>
          <Typography
            variant="P10R"
            color={tokens.theme.fg.structural.secondary}
          >
            {dayjs(recommendation.latest.appliedAt).format(
              DATE_TIME_WITH_OFFSET
            )}
          </Typography>
          {dashesOrNum({ value: recommendation.latest[type], roundBy: 3 })}
          &nbsp;
          {computeResourceUnitsMap[type]}
        </Stack>
      }
    />
  );
};
